import { useEffect, useState } from "react";
import { useTheme } from "next-themes";
import {
  Brain,
  Building2,
  CalendarDays,
  ChevronLeft,
  LogOut,
  Monitor,
  Moon,
  Newspaper,
  Sun,
  TrendingUp,
  User,
  X,
} from "lucide-react";
import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import { Separator } from "@/components/ui/separator";
import { ToggleGroup, ToggleGroupItem } from "@/components/ui/toggle-group";
import {
  Dialog,
  DialogClose,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogTrigger,
} from "@/components/ui/dialog";
import { cn } from "@/lib/utils";
import type { ViewId } from "@/components/layout/AppShell";

const VIEWS: { id: ViewId; label: string; Icon: typeof Building2 }[] = [
  { id: "companies", label: "Companies", Icon: Building2 },
  { id: "predictions", label: "Predictions", Icon: Brain },
  { id: "news", label: "News", Icon: Newspaper },
  { id: "calendar", label: "Calendar", Icon: CalendarDays },
];

function MarketClock() {
  const [now, setNow] = useState(() => new Date());
  useEffect(() => {
    const id = setInterval(() => setNow(new Date()), 30_000);
    return () => clearInterval(id);
  }, []);
  return (
    <div className="text-[10px] font-mono text-muted-foreground tabular-nums tracking-wider mr-1 hidden md:block">
      NYSE · MKT OPEN · {now.toLocaleTimeString([], { hour: "2-digit", minute: "2-digit" })}
    </div>
  );
}

function ThemeToggle() {
  const { theme, setTheme } = useTheme();
  return (
    <ToggleGroup
      type="single"
      value={theme ?? "system"}
      onValueChange={(v) => v && setTheme(v)}
      className="rounded-[4px] border border-border bg-muted/30 p-0.5 gap-0.5"
    >
      <ToggleGroupItem value="light" className="h-5 w-5 p-0" aria-label="Light">
        <Sun className="!h-3 !w-3" />
      </ToggleGroupItem>
      <ToggleGroupItem value="dark" className="h-5 w-5 p-0" aria-label="Dark">
        <Moon className="!h-3 !w-3" />
      </ToggleGroupItem>
      <ToggleGroupItem value="system" className="h-5 w-5 p-0" aria-label="System">
        <Monitor className="!h-3 !w-3" />
      </ToggleGroupItem>
    </ToggleGroup>
  );
}

export function TopBar({
  view,
  setView,
  detailOpen = false,
  onBack,
  detailTitle,
}: {
  view: ViewId;
  setView: (v: ViewId) => void;
  detailOpen?: boolean;
  onBack?: () => void;
  detailTitle?: string | null;
}) {
  return (
    <div className="h-9 border-b border-border bg-background flex items-center px-3 gap-3 shrink-0">
      {detailOpen ? (
        <button
          type="button"
          onClick={onBack}
          className="md:hidden inline-flex items-center gap-1 h-7 -ml-1 pl-1 pr-2 rounded text-[12px] font-medium hover:bg-accent"
          aria-label="Back"
        >
          <ChevronLeft className="h-4 w-4" />
          {detailTitle && (
            <span className="font-mono font-semibold truncate max-w-[60vw]">{detailTitle}</span>
          )}
        </button>
      ) : null}

      <div
        className={cn(
          "items-center gap-1.5 mr-1",
          detailOpen ? "hidden md:flex" : "flex",
        )}
      >
        <div className="h-5 w-5 rounded-[4px] bg-foreground flex items-center justify-center">
          <TrendingUp className="!h-3 !w-3 text-background" />
        </div>
        <div className="hidden md:block font-semibold text-[12px] tracking-tight whitespace-nowrap">
          Stock Shooter
        </div>
        <Badge variant="outline" className="hidden md:inline-flex ml-0.5 text-[10px] font-mono px-1.5 py-0">
          v0.4
        </Badge>
      </div>

      <ToggleGroup
        type="single"
        value={view}
        onValueChange={(v) => v && setView(v as ViewId)}
        className="hidden md:flex rounded-[4px] border border-border bg-muted/30 p-0.5 gap-0.5"
      >
        {VIEWS.map(({ id, label, Icon }) => (
          <ToggleGroupItem
            key={id}
            value={id}
            className="h-6 px-2 text-[11px] font-medium gap-1.5"
          >
            <Icon className="!h-3 !w-3" /> {label}
          </ToggleGroupItem>
        ))}
      </ToggleGroup>

      <div className="ml-auto flex items-center gap-1.5">
        <MarketClock />
        <ThemeToggle />
        <Dialog>
          <DialogTrigger asChild>
            <Button
              variant="outline"
              size="icon"
              className="h-6 w-6 rounded-full"
              title="Account"
            >
              <User className="!h-3 !w-3" />
            </Button>
          </DialogTrigger>
          <DialogContent className="sm:max-w-[320px] p-0">
            <DialogHeader className="px-3 py-2 border-b border-border flex-row items-center justify-between space-y-0">
              <DialogTitle className="text-[12px] font-semibold">Account</DialogTitle>
              <DialogClose className="h-5 w-5 inline-flex items-center justify-center rounded text-muted-foreground hover:text-foreground hover:bg-accent">
                <X className="h-3 w-3" />
              </DialogClose>
            </DialogHeader>
            <div className="p-3 space-y-3">
              <div className="flex items-center gap-2">
                <div className="h-8 w-8 rounded-full bg-foreground text-background inline-flex items-center justify-center text-[12px] font-semibold">
                  SS
                </div>
                <div>
                  <div className="text-[12px] font-medium">you@stock-shooter.local</div>
                  <div className="text-[10px] text-muted-foreground font-mono">Local · v0.4</div>
                </div>
              </div>
              <Separator />
              <Button variant="outline" size="sm" className="w-full" disabled>
                <LogOut className="h-3 w-3" /> Log out
              </Button>
            </div>
          </DialogContent>
        </Dialog>
      </div>
    </div>
  );
}
